import React from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'
import { translate } from '../i18n'
import { DAY_ABBREVIATIONS, DAY_LABELS } from '@/constants'

export function formatRepeatSummary(repeatDays: number[]): string {
  switch (repeatDays.length) {
    case 0:
      return translate('loungeGameSettingsScreen.never')
    case 7:
      return translate('loungeGameSettingsScreen.everyDay')
    case 1:
      return `Every ${DAY_LABELS[repeatDays[0]]}`
    default:
      return repeatDays.map((d) => DAY_ABBREVIATIONS[d]).join(', ')
  }
}

function SquareCheckbox({ selected, onPress }: { selected: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected }}
      hitSlop={8}
      style={[styles.checkbox, selected && styles.checkboxSelected]}
    >
      {selected && <DS.Icon name="Check" size={14} color="Global/White" />}
    </Pressable>
  )
}

export function ScheduleRepeatSection({
  repeatDays,
  expanded,
  onToggleExpanded,
  onToggleDay
}: {
  repeatDays: number[]
  expanded: boolean
  onToggleExpanded: () => void
  onToggleDay: (day: number) => void
}) {
  return (
    <View style={styles.scheduleSection}>
      <DS.Text variant="Caption/Bold" style={styles.sectionLabel}>
        {translate('loungeGameSettingsScreen.schedule')}
      </DS.Text>
      <View style={styles.scheduleRow}>
        <DS.Text variant="Body Medium/Regular" style={styles.scheduleRowLabel}>
          {translate('loungeGameSettingsScreen.repeat')}
        </DS.Text>
        <Pressable
          style={styles.repeatButton}
          onPress={onToggleExpanded}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
        >
          <DS.Text variant="Body Small/Medium" numberOfLines={1} style={styles.repeatSummary}>
            {formatRepeatSummary(repeatDays)}
          </DS.Text>
          <DS.Icon name={expanded ? 'Arrow---down' : 'Arrow---right'} size={16} color="Global/Black" />
        </Pressable>
      </View>
      {expanded &&
        DAY_LABELS.map((day, index) => (
          <View key={day} style={styles.scheduleRow}>
            <DS.Text variant="Body Medium/Regular" style={styles.scheduleRowLabel}>
              {`Every ${day}`}
            </DS.Text>
            <SquareCheckbox selected={repeatDays.includes(index)} onPress={() => onToggleDay(index)} />
          </View>
        ))}
    </View>
  )
}

const styles = StyleSheet.create({
  scheduleSection: {
    gap: 8
  },
  sectionLabel: {
    color: DS.palette['Brand/Dark Grey'],
    textTransform: 'uppercase'
  },
  scheduleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 32,
    gap: 16
  },
  scheduleRowLabel: {
    flex: 1
  },
  repeatButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    maxWidth: 200
  },
  repeatSummary: {
    flexShrink: 1
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: DS.palette['Global/Black'],
    alignItems: 'center',
    justifyContent: 'center'
  },
  checkboxSelected: {
    backgroundColor: DS.palette['Global/Black']
  }
})
